import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Loader2, Stamp } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE as API } from '../../lib/apiBase';
import { safeJson } from '../../lib/safeJson';

const h = () => {
  const t = localStorage.getItem('hrms-token');
  return t ? { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
};

export default function DirectorApprovalDialog({ form, open, onClose, onDone }) {
  const [agreement, setAgreement] = useState('agree');
  const [reasons, setReasons] = useState('');
  const [decision, setDecision] = useState('recommended');
  const [remarks, setRemarks] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAgreement('agree'); setReasons(''); setDecision('recommended'); setRemarks('');
  }, [open]);

  if (!form) return null;
  const ev = form.evaluation || {};

  const submit = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/faculty-appraisal/forms/${form.form_id}/director-approve`, {
        method: 'PUT', headers: h(), credentials: 'include',
        body: JSON.stringify({ agreement, reasons, decision, remarks }),
      });
      const d = await safeJson(res);
      if (!res.ok) throw new Error(d.detail || 'Failed');
      toast.success(d.message);
      onDone?.();
      onClose();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="director-approval-dialog">
        <DialogHeader>
          <DialogTitle>Part III &amp; Final Remarks — {form.employee_name}</DialogTitle>
          <DialogDescription>{form.department} · A.Y. {form.academic_year}</DialogDescription>
        </DialogHeader>

        <div className="rounded-md bg-slate-50 dark:bg-slate-800 p-4 space-y-1 text-sm" data-testid="director-eval-summary">
          <p className="text-xs uppercase text-slate-500">
            {form.is_hod ? 'Evaluation by Director' : 'Evaluation by Head of Department'}
          </p>
          <p className="text-2xl font-bold">{ev.total_points ?? 0}
            <span className="text-sm font-normal text-slate-500"> / {ev.max_points || 700}</span>
            <Badge className={`ml-3 ${ev.eligible ? 'bg-emerald-500' : 'bg-red-500'}`}>
              {ev.eligible ? 'Eligible' : 'Below minimum'}
            </Badge>
          </p>
          <p className="text-xs text-slate-500">Evaluated by {ev.evaluator_name || '—'} on {(ev.evaluated_at || '').slice(0, 10) || '—'}</p>
          {ev.overall_remark && <p className="pt-2 text-slate-600 dark:text-slate-300">{ev.overall_remark}</p>}
        </div>

        <div className="space-y-2">
          <Label className="text-sm font-semibold">Part III — Reviewing Officer</Label>
          <RadioGroup value={agreement} onValueChange={setAgreement} className="space-y-2">
            <div className="flex items-center gap-2">
              <RadioGroupItem value="agree" id="ag-agree" data-testid="agreement-agree" />
              <Label htmlFor="ag-agree" className="font-normal cursor-pointer">
                I agree with the assessment of the {form.is_hod ? 'evaluation' : 'Head of Department'}
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="differ" id="ag-differ" data-testid="agreement-differ" />
              <Label htmlFor="ag-differ" className="font-normal cursor-pointer">
                I differ with the assessment for the following reasons
              </Label>
            </div>
          </RadioGroup>
          {agreement === 'differ' && (
            <Textarea rows={3} placeholder="Reasons for differing..." value={reasons}
              onChange={e => setReasons(e.target.value)} data-testid="differ-reasons" />
          )}
        </div>

        <div className="space-y-2 pt-2">
          <Label className="text-sm font-semibold">Director — Final Remarks</Label>
          <RadioGroup value={decision} onValueChange={setDecision} className="space-y-2">
            <div className="flex items-center gap-2">
              <RadioGroupItem value="recommended" id="dec-rec" data-testid="decision-recommended" />
              <Label htmlFor="dec-rec" className="font-normal cursor-pointer">
                Recommended for annual increment / promotion for the next grade
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="not_recommended" id="dec-not" data-testid="decision-not-recommended" />
              <Label htmlFor="dec-not" className="font-normal cursor-pointer">
                Not recommended for annual increment / promotion
              </Label>
            </div>
          </RadioGroup>
          <Textarea rows={3} placeholder="Remarks (optional)" value={remarks}
            onChange={e => setRemarks(e.target.value)} data-testid="director-remarks" />
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t border-slate-200 dark:border-slate-700">
          <Button variant="outline" onClick={onClose} data-testid="director-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="director-confirm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Stamp className="w-4 h-4 mr-1" />}
            Confirm Final Approval
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
